import { useReadOnly } from '../../Hooks/useReadOnly';
import { Permissions } from '../../Security/permissions';
// StoreDetails.js
import React, { useState, useEffect, useRef } from 'react';
import { Container, Button, Card } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import './StoreDetails.scss';
import { InteractionType } from '@azure/msal-browser';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import { CatalogDataService } from '../../Services/CatalogsDataService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import noImage from '../../Assets/img/catalogs/no-store.png';

const StoreDetails = () => {
    const readOnly = useReadOnly(Permissions.DataManagement.Stores);
    const { id } = useParams();
    const navigate = useNavigate();
    const { instance, accounts, inProgress } = useMsal();
    const [store, setStore] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (inProgress === "none" && accounts.length > 0) {
            fetchStoreDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inProgress, accounts]);

    const fetchStoreDetails = async () => {
        try {
            const request = { ...loginRequest, account: accounts[0] };
            const tokenResponse = await instance.acquireTokenSilent(request);
            const response = await CatalogDataService.getStoreById(id, tokenResponse.accessToken);
            if (response && response.data) {
                setStore(response.data);
            }
        } catch (error) {
            console.error('Error fetching store details:', error);
        }
    };

    // Trigger file input when "Edit Image" is clicked
    const handleEditImageClick = () => {
        fileInputRef.current.click();
    };

    // Handle file selection and upload
    const handleFileChange = async (e) => {
        if (readOnly) { e?.preventDefault?.(); return; }
        const file = e.target.files[0];
        if (!file) return;
        try {
            const request = { ...loginRequest, account: accounts[0] };
            const tokenResponse = await instance.acquireTokenSilent(request);
            // Call the API endpoint to update the product image
            const response = await CatalogDataService.updateStoreImage(id, file, tokenResponse.accessToken);
            if (response && response.status === 200) {
                // Refresh product details to display the new image
                fetchStoreDetails();
            }
        } catch (error) {
            console.error('Error updating product image:', error);
        }
    };

    if (!store) return <div>Loading...</div>;

    return (
        <Container className="store-details">
            {/* The details container */}
            <div className="details-container">
                {/* Top Section (2/3 height) */}
                <div className="top-section">
                    {/* Left: Image Section (1/3 width) */}
                    <div className="image-section">
                        <div className="top-buttons d-flex">
                            <Button 
                                variant="primary" 
                                size="lg" 
                                className="back-button text-white" 
                                onClick={() => navigate(-1)}
                            >
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Button>
                        </div>
                        <div className="image-container">
                            <img 
                                src={store.blobImageURL || noImage} 
                                alt={store.storeName} 
                                onError={(e) => { e.target.onerror = null; e.target.src = noImage; }} 
                            />
                        </div>
                        <div className='d-flex justify-content-end'>
                            <Button disabled={readOnly} variant="primary" className="edit-image-button" onClick={handleEditImageClick}>
                                Edit Image
                            </Button>
                            {/* Hidden file input */}
                            <input disabled={readOnly}
                                type="file"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                        </div>
                    </div>
                    {/* Right: Information Section (2/3 width) */}
                    <div className="info-section">
                        <small className="key-account text-muted">{store.keyAccount}</small>
                        <h2 className="title">{store.storeName}</h2>
                        <h3 className="subtitle">{store.fullAddress}</h3>
                        <strong>About this store</strong>
                        <ul className="attributes d-flex flex-column flex-wrap">
                            <li>Retail Environment: {store.retailEnvironment}</li>
                            <li>Store Size: {store.storeSize}</li>
                            <li>Store Format: {store.storeFormat}</li>
                            <li>Urbanicity: {store.urbanicity}</li>
                            <li>Open Date: {store.openDate ? new Date(store.openDate).toLocaleDateString() : ''}</li>
                            <li>Closure Date: {store.closureDate ? new Date(store.closureDate).toLocaleDateString() : ''}</li>
                            <li>Is Franchise: {store.isFranchise ? 'Yes' : 'No'}</li>
                            <li>Data Sharing Agreement Type: {store.dataSharingAgreementType}</li>
                            <li>Shared Data Attributes: {store.sharedDataAttributes}</li>
                            <li>Omnichannel Enabled: {store.isOmnichannelEnabled ? 'Yes' : 'No'}</li>
                            <li>Created: {store.createdDate ? new Date(store.createdDate).toLocaleDateString() : ''}</li>
                            <li>Modified: {store.modifiedDate ? new Date(store.modifiedDate).toLocaleDateString() : ''}</li>
                            <li>Internal Store Code: {store.internalStoreCode}</li>
                        </ul>
                        <div className="d-flex justify-content-end">
                            <Button variant="primary" className="edit-button">Edit</Button>
                        </div>
                    </div>
                </div>
                {/* Bottom Section (1/3 height) */}
                <div className="bottom-section">
                    <h4>More about this store</h4>
                    <div className="charts-container">
                        <Card className="chart-card"><h6>Store Traffic</h6></Card>
                        <Card className="chart-card"><h6>Sales Performance</h6></Card>
                        <Card className="chart-card"><h6>Customer Satisfaction</h6></Card>
                        {/* Additional chart cards can be added; the container scrolls horizontally */}
                    </div>
                </div>
            </div>
        </Container>
    );
};

const StoreDetailsPage = () => {
    return (
        <MsalAuthenticationTemplate 
            interactionType={InteractionType.Redirect} 
            authenticationRequest={loginRequest}
        >
            <PageLayout2 title="Store Details">
                <StoreDetails />
            </PageLayout2>
        </MsalAuthenticationTemplate>
    );
};

export default StoreDetailsPage;
